import { formatNOK } from '@polf/shared/locale'
import { Navigate, useOutletContext, useParams } from 'react-router-dom'

import type { CurrentPlayerCtx } from '../PlayerShell'
import { payoutAmountsNOK } from '../util/payouts'
import PayoutList from './PokerSeat/components/PayoutList'
import { usePokerView } from './PokerSeat/hooks/usePokerView'

/**
 * "Premier" tab — the prize ladder on its own page, reachable in every poker
 * state (the inline PayoutList on Bordet only appears in the active/eliminated
 * views). The tab itself is only shown when the event has payouts, so this page
 * assumes payouts exist; a stale/direct URL to a payout-less event bounces to
 * Bordet rather than showing an empty page.
 *
 * Once the player has a FINAL finishing position (knocked out or tournament
 * over) a "Du vant" banner confirms their actual result + prize and the ladder
 * highlights that row; while still live it highlights the probable finish.
 */
const PokerPayouts = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const { view, state, payJump } = usePokerView(player.polfEvent.hasPokerPhase)

  if (state === 'no-poker') {
    return (
      <section className="px-6 py-12 text-center">
        <h1 className="font-display text-xl font-bold text-green-900 mb-2">
          Premier
        </h1>
        <p className="text-ink-500 text-sm">
          Pokerfase er ikke aktivert for denne turneringen.
        </p>
      </section>
    )
  }

  if (state === 'loading') {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster premier…
      </section>
    )
  }

  if (state === 'error' || !view) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste premier.
      </section>
    )
  }

  // Tab is hidden without payouts, but guard the direct-URL case → back to Bordet.
  if (!view.usePayouts || view.payouts.length === 0) {
    return <Navigate to={`/s/${playerKey}/poker`} replace />
  }

  const finishingPosition = view.seat?.finishingPosition ?? null
  const highlightPosition =
    finishingPosition ??
    (payJump?.kind === 'secured' ? payJump.positionIfNow : null)

  const krMap =
    view.prizePool != null && view.prizePool > 0
      ? payoutAmountsNOK(view.payouts, view.prizePool)
      : null
  const myKr =
    finishingPosition != null ? krMap?.get(finishingPosition) ?? null : null
  const isWinner = finishingPosition === 1

  return (
    <section className="pb-4">
      <div className="px-4 pt-4 pb-2">
        <h1 className="font-display text-xl font-bold text-green-900">
          Premier
        </h1>
      </div>

      {finishingPosition != null ? (
        <div
          className={`mx-2 my-3 rounded-lg p-4 shadow flex flex-col items-center gap-1 ${
            isWinner ? 'bg-gold text-ink-900' : 'bg-green-900 text-paper'
          }`}
        >
          <div
            className={`text-[11px] up ${
              isWinner ? 'text-ink-700' : 'text-green-100'
            }`}
          >
            {isWinner
              ? 'Du vant turneringen'
              : `Du kom på ${finishingPosition}. plass`}
          </div>
          {myKr != null ? (
            <div className="text-base">
              Premie{' '}
              <span
                className={`font-mono num font-bold ${
                  isWinner ? 'text-ink-900' : 'text-gold'
                }`}
              >
                {formatNOK(myKr)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <PayoutList
        payouts={view.payouts}
        prizePool={view.prizePool}
        highlightPosition={highlightPosition}
      />
    </section>
  )
}

export default PokerPayouts
